import React from 'react'

import Content, { Theme as ContentTheme } from 'components/base/Content'
import Paragraph, { Theme as ParagraphTheme }  from 'components/base/Paragraph'
import ButtonRow from 'components/base/Button/Row'
import Button, { Theme as ButtonTheme } from 'components/base/Button'
import Link, { Theme as LinkTheme } from 'components/base/Link'
import Form from 'components/base/Form'
import InputGroup from 'components/base/Form/InputGroup'
import Label from 'components/base/Form/Label'
import Input, { Type as InputType } from 'components/base/Form/Input'
import Option from 'components/base/Form/Input/Select/Option'
import {
  QuoteIconApple,
  QuoteIconMan,
  QuoteIconRing,
} from 'components/base/Icon/styles'
import SectionDivider from 'components/base/SectionDivider'
import Tooltip from 'components/blocks/Tooltip'

import QuotingWrapper, { Theme as QuotingWrapperTheme } from 'components/blocks/QuotingWrapper'

const QuotingAutoMissingDriver: React.FC = (): React.ReactElement => (
  <QuotingWrapper noPadding={true} theme={QuotingWrapperTheme.auto}>
    <Content margined={true} center={true}>
      <Paragraph theme={ParagraphTheme.intro} short={true}>
        We found a driver missing from the information you submitted. Please
        review and confirm the entries below.
      </Paragraph>
    </Content>
    <Form>
      <Content>
        <InputGroup>
          <Label htmlFor='ratedDriver'>
            Include as Rated Driver on Policy{' '}
            <Tooltip>Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt.</Tooltip>
          </Label>
          <Input type={InputType.select} name='ratedDriver'>
            <Option value={false}>No</Option>
            <Option value={true}>Yes</Option>
          </Input>
        </InputGroup>
        <InputGroup>
          <Label htmlFor='reason'>
            Reason for not Rating this Driver{' '}
            <Tooltip>Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt.</Tooltip>
          </Label>
          <Input type={InputType.select} rules='required' name='reason'>
            <Option value='hasOwnCar'>Has Own Car and Insurance</Option>
            <Option value='learnersPermit'>Learners Permit</Option>
            <Option value='licenseRevoked'>License Revoked/Suspended</Option>
            <Option value='neverLicensed'>
              Never Licensed/Does Not Drive
            </Option>
          </Input>
        </InputGroup>
        <InputGroup>
          <Label htmlFor='relationshipToPrimary'>
            Relationship to Primary Driver
          </Label>
          <Input type={InputType.select} name='relationshipToPrimary'>
            <Option value='husband'>Husband</Option>
            <Option value='wife'>Wife</Option>
            <Option value='child'>Child</Option>
          </Input>
        </InputGroup>
      </Content>
      <Content theme={ContentTheme.paper}>
        <SectionDivider
          icon={<QuoteIconMan />}
          title='Name &amp; License Info'
        />
        <InputGroup>
          <Button theme={ButtonTheme.scan}>
            <strong>Scan License</strong> to populate info below
          </Button>
        </InputGroup>
        <InputGroup>
          <Label htmlFor='firstName'>First Name</Label>
          <Input type={InputType.text} name='firstName' />
        </InputGroup>
        <InputGroup>
          <Label htmlFor='lastName'>Last Name</Label>
          <Input type={InputType.text} name='lastName' />
        </InputGroup>
        <InputGroup>
          <Label htmlFor='licenseNumber'>License Number</Label>
          <Input type={InputType.text} name='licenseNumber' />
        </InputGroup>
        <InputGroup>
          <Label htmlFor='licenseState'>License State</Label>
          <Input type={InputType.text} name='licenseState' />
        </InputGroup>
        <InputGroup>
          <Label htmlFor='DOB'>Date of Birth</Label>
          <Input type={InputType.text} name='DOB' />
        </InputGroup>
        <InputGroup>
          <Label htmlFor='gender'>Gender</Label>
          <Input type={InputType.text} name='gender' />
        </InputGroup>
        <InputGroup>
          <Label htmlFor='licenceStatus'>
            Has your license been suspended or revoked in the last 5 years?
          </Label>
          <Input
            type={InputType.select}
            id='licenceStatus'
            name='licenceStatus'
            isRequired={true}
          >
            <Option value='' disabled={true} selected={true} hidden={true}>
              Choose One
            </Option>
            <Option value='optOne'>Option One</Option>
            <Option value='optTwo'>Option Two</Option>
          </Input>
        </InputGroup>
      </Content>
      <Content margined={true}>
        <SectionDivider
          icon={<QuoteIconApple />}
          title='Education &amp; Career'
        />
        <InputGroup>
          <Label htmlFor='education'>Level of Education</Label>
          <Input type={InputType.select} id='education' name='education'>
            <Option value='noHighSchool'>No high school diploma</Option>
            <Option value='highSchool' selected={true}>
              High School Diploma
            </Option>
            <Option value='someCollege'>Some College</Option>
            <Option value='vocational'>Vocational/technical college</Option>
            <Option value='associates'>Associates</Option>
            <Option value='bachelors'>Bachelors</Option>
            <Option value='masters'>Masters</Option>
            <Option value='phd'>Phd</Option>
            <Option value='medicalDegree'>Medical degree</Option>
            <Option value='lawDegree'>Law degree</Option>
          </Input>
        </InputGroup>
        <InputGroup>
          <Label htmlFor='occupationIndustry'>Occupation Industry</Label>
          <Input
            type={InputType.select}
            id='occupationIndustry'
            name='occupationIndustry'
            required={true}
          >
            <Option value='' disabled={true} selected={true} hidden={true}>
              Select the option that best describes you
            </Option>
            <Option value='homeMaker'>Homemaker/house person</Option>
            <Option value='retired'>Retired</Option>
            <Option value='disabled'>Disabled</Option>
            <Option value='student'>Student</Option>
            <Option value='agriculture'>Agriculture/Forestry/Fishing</Option>
            <Option value='art'>Art/Design/Media</Option>
            <Option value='banking'>Banking/Finance/Real Estate</Option>
            <Option value='business'>Business/Sales/Office</Option>
            <Option value='construction'>Construction/Energy Trades</Option>
            <Option value='education'>Education/Library</Option>
            <Option value='engineer'>Engineer/Architect/Science/Math</Option>
            <Option value='government'>Government/Military</Option>
            <Option value='informationTechnology'>
              Information Technology
            </Option>
            <Option value='insurance'>Insurance</Option>
            <Option value='legal'>Legal/Law Enforcement/Security</Option>
            <Option value='maintenance'>
              Maintenance/Repair/Housekeeping
            </Option>
            <Option value='manufacturing'>Manufacturing/Production</Option>
            <Option value='medical'>Medical/Social Services/Religion</Option>
            <Option value='personalCare'>Personal Care/Service</Option>
            <Option value='restaurant'>Restaurant/Hotel Services</Option>
            <Option value='sport'>Sport/Recreation</Option>
            <Option value='travel'>Travel/Transportation/Warehousing</Option>
          </Input>
        </InputGroup>
        <InputGroup>
          <Label htmlFor='occupationTitle'>Occupation Title</Label>
          <Input type={InputType.select} id='occupationTitle' name='occupationTitle'>
            <Option value='consultant'>Consultant</Option>
            <Option value='executive'>Executive</Option>
            <Option value='manager'>Manager</Option>
            <Option value='other'>Other</Option>
          </Input>
        </InputGroup>
      </Content>
      <Content theme={ContentTheme.gray}>
        <SectionDivider
          icon={<QuoteIconRing />}
          title='Relationship Status'
        />
        <InputGroup>
          <Label htmlFor='maritalStatus'>
            Marital Status{' '}
            <Tooltip>Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt.</Tooltip>
          </Label>
          <Input
            type={InputType.select}
            id='maritalStatus'
            name='maritalStatus'
            isRequired={true}
          >
            <Option value='' disabled={true} selected={true} hidden={true}>
              Choose One
            </Option>
            <Option value='married'>Married</Option>
            <Option value='single'>Single</Option>
            <Option value='other'>Other</Option>
          </Input>
        </InputGroup>
        <ButtonRow>
          <Link
            center={true}
            wide={true}
            theme={LinkTheme.button}
            to='/quote/new/auto/rate-calls/1.5'
          >
            Next
          </Link>
        </ButtonRow>
      </Content>
    </Form>
  </QuotingWrapper>
)

export default QuotingAutoMissingDriver
